import React from 'react';
import { ReactComponent as InfoCircleIcon } from '../../../icons/info-circle.svg';
import { ReactComponent as InboxIcon } from '../../../icons/inbox-01.svg';
import { InboxAccount } from './InboxesTab';

interface DraftingTabProps {
  accounts: InboxAccount[];
  /** oauthId whose auto-draft toggle is mid-flight, or null. */
  togglingAutoDraftOauthId: number | null;
  onToggleAutoDraft: (account: InboxAccount) => void;
  /** The assistant's own mailbox, used for the never-sends-as-you notice. */
  assistantEmail: string | null;
  /** What to call the assistant in copy. Falls back to a generic label. */
  assistantName: string | null;
}

const DraftingTab: React.FC<DraftingTabProps> = ({
  accounts,
  togglingAutoDraftOauthId,
  onToggleAutoDraft,
  assistantEmail,
  assistantName,
}) => {
  const assistant = assistantName || 'Your secretary';
  return (
    <div className='inbox-tab drafting-tab'>
      <div className='section-header'>
        <h2>Drafting</h2>
      </div>

      <div className='drafting-intro'>
        <p className='drafting-blurb'>
          Manage how {assistant} drafts emails for you. Nothing is ever sent —
          drafts are yours to edit, send or bin.
        </p>
        {assistantEmail && (
          <div className='drafting-notice' data-testid='drafting-notice'>
            <InfoCircleIcon />
            <span>
              {assistant} will never send an email from your account, they
              always use their own email
              <strong>{assistantEmail}</strong>
            </span>
          </div>
        )}
      </div>

      <div className='drafting-section'>
        <span className='drafting-section-label'>Manage inbox drafting</span>

        {accounts.length === 0 ? (
          <div className='app-empty-state' data-testid='drafting-empty'>
            <InboxIcon className='app-empty-icon' />
            <p className='app-empty-title'>
              You don&apos;t have any connected inboxes
            </p>
            <p className='app-empty-blurb'>
              Connect an inbox on the Inboxes tab to have replies drafted for
              you.
            </p>
          </div>
        ) : (
          <div className='drafting-inbox-list'>
            {accounts.map((account) => {
              const disconnected = account.isActive === false;
              return (
                <div
                  key={account.oauthId}
                  className={`drafting-inbox-row${disconnected ? ' is-disconnected' : ''}`}
                  data-testid={`drafting-inbox-${account.oauthId}`}
                >
                  <span className='drafting-inbox-email'>{account.email}</span>
                  {disconnected && (
                    <span className='drafting-inbox-note'>Disconnected</span>
                  )}
                  <button
                    type='button'
                    className={`toggle-switch ${account.autoDraftReplies && !disconnected ? 'on' : 'off'}`}
                    onClick={() => onToggleAutoDraft(account)}
                    disabled={togglingAutoDraftOauthId !== null || disconnected}
                    aria-label={`Toggle draft replies for ${account.email}`}
                    data-testid={`toggle-auto-draft-${account.oauthId}`}
                  >
                    <span className='toggle-thumb' />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DraftingTab;
